// Framework
import { createContext, createPlugin, usePrefersReducedMotion, useStorage } from '@vuetify/v0'
import { IN_BROWSER } from '@vuetify/v0/constants'
import { isNullOrUndefined } from '@vuetify/v0/utilities'

import { isCodingCli } from '@/constants/codingCli'

// Utilities
import { nextTick, shallowRef, toRef, watch } from 'vue'

// Types
import type { CodingCli } from '@/constants/codingCli'
import type { StorageContext } from '@vuetify/v0'
import type { Ref, ShallowRef } from 'vue'

export type { CodingCli } from '@/constants/codingCli'

export type PackageManager = 'pnpm' | 'npm' | 'yarn' | 'bun'

export type CodeSize = 'small' | 'medium' | 'large'

export const CODE_SIZES: CodeSize[] = ['small', 'medium', 'large']

export interface DocSettings {
  lineWrap: boolean
  codeSize: CodeSize
  reduceMotion: 'system' | 'on' | 'off'
  packageManager: PackageManager
  codingCli: CodingCli
  showInlineApi: boolean
  showSkillFilter: boolean
  showThemeToggle: boolean
  showSocialLinks: boolean
  collapsibleNav: boolean
  showDotGrid: boolean
  dotGridIntensity: number
  dotGridCoverage: number

  showMeshGrid: boolean
  showMeshTransition: boolean
  showBgGlass: boolean
  styledScrollbars: boolean
}

export interface SettingsContext {
  isOpen: ShallowRef<boolean>
  lineWrap: ShallowRef<boolean>
  codeSize: ShallowRef<CodeSize>
  reduceMotion: ShallowRef<DocSettings['reduceMotion']>
  packageManager: ShallowRef<DocSettings['packageManager']>
  codingCli: ShallowRef<DocSettings['codingCli']>
  prefersReducedMotion: Ref<boolean>
  /** User's actual reduced motion preference (ignores tour override) */
  userPrefersReducedMotion: Ref<boolean>
  /** Temporarily force reduced motion (e.g., during tours) */
  forceReducedMotion: ShallowRef<boolean>
  showInlineApi: ShallowRef<boolean>
  showSkillFilter: ShallowRef<boolean>
  showThemeToggle: ShallowRef<boolean>
  showSocialLinks: ShallowRef<boolean>
  collapsibleNav: ShallowRef<boolean>
  showDotGrid: ShallowRef<boolean>
  dotGridIntensity: ShallowRef<number>
  dotGridCoverage: ShallowRef<number>

  showMeshGrid: ShallowRef<boolean>
  showMeshTransition: ShallowRef<boolean>
  showBgGlass: ShallowRef<boolean>
  styledScrollbars: ShallowRef<boolean>
  /** Surface background class tracking the glass setting — bind it instead of re-deriving the ternary. */
  surface: Ref<string>
  hasChanges: ShallowRef<boolean>
  open: () => void
  close: () => void
  toggle: () => void
  reset: () => void
}

const DEFAULTS: DocSettings = {
  lineWrap: false,
  codeSize: 'medium',
  reduceMotion: 'system',
  packageManager: 'pnpm',
  codingCli: 'grok',
  showInlineApi: false,
  showSkillFilter: true,
  showThemeToggle: true,
  showSocialLinks: true,
  collapsibleNav: true,
  showDotGrid: true,
  dotGridIntensity: 0.85,
  dotGridCoverage: 15,

  showMeshGrid: true,
  showMeshTransition: true,
  showBgGlass: true,
  styledScrollbars: false,
}

// Create context
const [useSettingsContext, provideSettingsContext] = createContext<SettingsContext>('v0:docs:settings')

/**
 * Get reduced motion preference outside Vue component context.
 * Reads directly from localStorage for use in router scrollBehavior.
 */
export function getPrefersReducedMotion (): boolean {
  if (!IN_BROWSER) return false

  const stored = localStorage.getItem('v0:reduceMotion')
  const parsed = stored ? JSON.parse(stored) : null
  const setting: DocSettings['reduceMotion'] = parsed === 'system' || parsed === 'on' || parsed === 'off' ? parsed : 'system'

  if (setting === 'system') {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }
  return setting === 'on'
}

/** Load a setting from storage into a ref */
function loadSetting<T> (
  storage: StorageContext,
  key: keyof DocSettings,
  ref: ShallowRef<T>,
) {
  const stored = storage.get<T>(key)
  if (!isNullOrUndefined(stored.value)) {
    ref.value = stored.value
  }
}

/**
 * Creates a new settings context instance.
 * Called once during app initialization.
 */
export function createSettingsContext (): SettingsContext {
  const storage = useStorage()
  const { matches: systemReducedMotion } = usePrefersReducedMotion()

  // Reactive state
  const isOpen = shallowRef(false)
  const forceReducedMotion = shallowRef(false)
  const lineWrap = shallowRef(DEFAULTS.lineWrap)
  const codeSize = shallowRef<CodeSize>(DEFAULTS.codeSize)
  const reduceMotion = shallowRef<DocSettings['reduceMotion']>(DEFAULTS.reduceMotion)
  const packageManager = shallowRef<DocSettings['packageManager']>(DEFAULTS.packageManager)
  const codingCli = shallowRef<DocSettings['codingCli']>(DEFAULTS.codingCli)
  const showInlineApi = shallowRef(DEFAULTS.showInlineApi)
  const showSkillFilter = shallowRef(DEFAULTS.showSkillFilter)
  const showThemeToggle = shallowRef(DEFAULTS.showThemeToggle)
  const showSocialLinks = shallowRef(DEFAULTS.showSocialLinks)
  const collapsibleNav = shallowRef(DEFAULTS.collapsibleNav)
  const showDotGrid = shallowRef(DEFAULTS.showDotGrid)
  const dotGridIntensity = shallowRef(DEFAULTS.dotGridIntensity)
  const dotGridCoverage = shallowRef(DEFAULTS.dotGridCoverage)

  const showMeshGrid = shallowRef(DEFAULTS.showMeshGrid)
  const showMeshTransition = shallowRef(DEFAULTS.showMeshTransition)
  const showBgGlass = shallowRef(DEFAULTS.showBgGlass)
  const styledScrollbars = shallowRef(DEFAULTS.styledScrollbars)

  // Load stored preferences
  loadSetting(storage, 'lineWrap', lineWrap)
  loadSetting(storage, 'codeSize', codeSize)
  loadSetting(storage, 'reduceMotion', reduceMotion)
  loadSetting(storage, 'packageManager', packageManager)
  const storedCli = storage.get<unknown>('codingCli')
  if (isCodingCli(storedCli.value)) codingCli.value = storedCli.value
  loadSetting(storage, 'showInlineApi', showInlineApi)
  loadSetting(storage, 'showSkillFilter', showSkillFilter)
  loadSetting(storage, 'showThemeToggle', showThemeToggle)
  loadSetting(storage, 'showSocialLinks', showSocialLinks)
  loadSetting(storage, 'collapsibleNav', collapsibleNav)
  loadSetting(storage, 'showDotGrid', showDotGrid)
  loadSetting(storage, 'dotGridIntensity', dotGridIntensity)
  loadSetting(storage, 'dotGridCoverage', dotGridCoverage)

  loadSetting(storage, 'showMeshGrid', showMeshGrid)
  loadSetting(storage, 'showMeshTransition', showMeshTransition)
  loadSetting(storage, 'showBgGlass', showBgGlass)
  loadSetting(storage, 'styledScrollbars', styledScrollbars)

  // Persist on change
  const settings = { lineWrap, codeSize, reduceMotion, packageManager, codingCli, showInlineApi, showSkillFilter, showThemeToggle, showSocialLinks, collapsibleNav, showDotGrid, dotGridIntensity, dotGridCoverage, showMeshGrid, showMeshTransition, showBgGlass, styledScrollbars }
  for (const [key, ref] of Object.entries(settings)) {
    watch(ref, val => storage.set(key, val))
  }

  // User's actual preference (ignores tour override - used for highlight animations)
  const userPrefersReducedMotion = toRef(() => {
    if (reduceMotion.value === 'system') return systemReducedMotion.value
    return reduceMotion.value === 'on'
  })

  // Computed effective reduced motion based on setting (or forced during tours)
  const prefersReducedMotion = toRef(() => {
    if (forceReducedMotion.value) return true
    return userPrefersReducedMotion.value
  })

  const surface = toRef(() => showBgGlass.value ? 'bg-glass-surface' : 'bg-surface')

  // Check if any setting differs from defaults
  const hasChanges = toRef(() => (
    lineWrap.value !== DEFAULTS.lineWrap ||
    codeSize.value !== DEFAULTS.codeSize ||
    reduceMotion.value !== DEFAULTS.reduceMotion ||
    packageManager.value !== DEFAULTS.packageManager ||
    codingCli.value !== DEFAULTS.codingCli ||
    showInlineApi.value !== DEFAULTS.showInlineApi ||
    showSkillFilter.value !== DEFAULTS.showSkillFilter ||
    showThemeToggle.value !== DEFAULTS.showThemeToggle ||
    showSocialLinks.value !== DEFAULTS.showSocialLinks ||
    collapsibleNav.value !== DEFAULTS.collapsibleNav ||
    showDotGrid.value !== DEFAULTS.showDotGrid ||
    dotGridIntensity.value !== DEFAULTS.dotGridIntensity ||
    dotGridCoverage.value !== DEFAULTS.dotGridCoverage ||
    showMeshGrid.value !== DEFAULTS.showMeshGrid ||
    showMeshTransition.value !== DEFAULTS.showMeshTransition ||
    showBgGlass.value !== DEFAULTS.showBgGlass ||
    styledScrollbars.value !== DEFAULTS.styledScrollbars
  ))

  // Track trigger element for focus restoration
  const triggerRef = shallowRef<HTMLElement | null>(null)

  function open () {
    if (IN_BROWSER) {
      triggerRef.value = document.activeElement as HTMLElement | null
    }
    isOpen.value = true
  }

  function close () {
    isOpen.value = false
    nextTick(() => {
      triggerRef.value?.focus()
      triggerRef.value = null
    })
  }

  function toggle () {
    if (isOpen.value) {
      close()
    } else {
      open()
    }
  }

  function reset () {
    lineWrap.value = DEFAULTS.lineWrap
    codeSize.value = DEFAULTS.codeSize
    reduceMotion.value = DEFAULTS.reduceMotion
    packageManager.value = DEFAULTS.packageManager
    codingCli.value = DEFAULTS.codingCli
    showInlineApi.value = DEFAULTS.showInlineApi
    showSkillFilter.value = DEFAULTS.showSkillFilter
    showThemeToggle.value = DEFAULTS.showThemeToggle
    showSocialLinks.value = DEFAULTS.showSocialLinks
    collapsibleNav.value = DEFAULTS.collapsibleNav
    showDotGrid.value = DEFAULTS.showDotGrid
    dotGridIntensity.value = DEFAULTS.dotGridIntensity
    dotGridCoverage.value = DEFAULTS.dotGridCoverage
    showMeshGrid.value = DEFAULTS.showMeshGrid
    showMeshTransition.value = DEFAULTS.showMeshTransition
    showBgGlass.value = DEFAULTS.showBgGlass
    styledScrollbars.value = DEFAULTS.styledScrollbars
  }

  return {
    isOpen,
    forceReducedMotion,
    lineWrap,
    codeSize,
    reduceMotion,
    packageManager,
    codingCli,
    prefersReducedMotion,
    userPrefersReducedMotion,
    showInlineApi,
    showSkillFilter,
    showThemeToggle,
    showSocialLinks,
    collapsibleNav,
    showDotGrid,
    dotGridIntensity,
    dotGridCoverage,
    showMeshGrid,
    showMeshTransition,
    showBgGlass,
    styledScrollbars,
    surface,
    hasChanges,
    open,
    close,
    toggle,
    reset,
  }
}

/**
 * Plugin to provide settings context at app level.
 */
export const SettingsPlugin = createPlugin({
  namespace: 'v0:docs:settings',
  provide: app => {
    const context = createSettingsContext()
    provideSettingsContext(context, app)
  },
})

/**
 * Access the settings context.
 * Must be called within a component where SettingsPlugin is installed.
 */
export function useSettings (): SettingsContext {
  return useSettingsContext()
}
